import * as THREE from 'three';

export interface YakEntity {
  group: THREE.Group;
  head: THREE.Mesh;
  basePos: THREE.Vector3;
  targetPos: THREE.Vector3;
  isEating: boolean;
  chewTimer: number;
}

export class AlpineYaks {
  public root: THREE.Group = new THREE.Group();
  public yaks: YakEntity[] = [];

  constructor(positions: THREE.Vector3[]) {
    this.root.name = 'AlpineYaks';

    for (const pos of positions) {
      const yak = this.createYak();
      yak.group.position.copy(pos);
      this.root.add(yak.group);
      this.yaks.push({
        group: yak.group,
        head: yak.head,
        basePos: pos.clone(),
        targetPos: pos.clone(),
        isEating: false,
        chewTimer: Math.random() * 2,
      });
    }
  }

  private createYak(): { group: THREE.Group; head: THREE.Mesh } {
    const group = new THREE.Group();

    const yakMat = new THREE.MeshStandardMaterial({
      color: 0x2b2118, // dark shaggy brown
      roughness: 0.9,
      flatShading: true,
    });

    const hornMat = new THREE.MeshStandardMaterial({
      color: 0xe2e8f0,
      roughness: 0.4,
    });

    // Body (heavy rectangular shape)
    const bodyGeo = new THREE.BoxGeometry(1.6, 1.2, 2.4);
    bodyGeo.translate(0, 1.1, 0);
    const body = new THREE.Mesh(bodyGeo, yakMat);
    body.castShadow = true;
    group.add(body);

    // 4 legs
    const legGeo = new THREE.CylinderGeometry(0.16, 0.18, 0.8, 5);
    legGeo.translate(0, 0.4, 0);
    const legPositions = [
      [-0.6, 0, 0.8],
      [0.6, 0, 0.8],
      [-0.6, 0, -0.8],
      [0.6, 0, -0.8],
    ];
    for (const [lx, ly, lz] of legPositions) {
      const leg = new THREE.Mesh(legGeo, yakMat);
      leg.position.set(lx, ly, lz);
      group.add(leg);
    }

    // Head
    const headGeo = new THREE.BoxGeometry(0.8, 0.8, 1.0);
    headGeo.translate(0, 0.4, 0.5);
    const head = new THREE.Mesh(headGeo, yakMat);
    head.position.set(0, 1.2, 1.2);
    group.add(head);

    // Left and Right Horns
    const hornGeo = new THREE.ConeGeometry(0.12, 0.7, 4);
    const leftHorn = new THREE.Mesh(hornGeo, hornMat);
    leftHorn.rotation.z = Math.PI / 3;
    leftHorn.rotation.x = -Math.PI / 6;
    leftHorn.position.set(-0.45, 0.7, 0.3);
    head.add(leftHorn);

    const rightHorn = new THREE.Mesh(hornGeo, hornMat);
    rightHorn.rotation.z = -Math.PI / 3;
    rightHorn.rotation.x = -Math.PI / 6;
    rightHorn.position.set(0.45, 0.7, 0.3);
    head.add(rightHorn);

    return { group, head };
  }

  public update(dt: number, time: number): void {
    for (let i = 0; i < this.yaks.length; i++) {
      const yak = this.yaks[i];

      // Idle grazing & head bob
      yak.chewTimer += dt;
      const chew = Math.sin(yak.chewTimer * 4.0) * 0.12;
      const bob = Math.sin(time * 1.5 + i * 2.0) * 0.08;

      yak.head.rotation.x = 0.2 + bob + chew;

      if (yak.isEating) {
        // Move towards target
        yak.group.position.lerp(yak.targetPos, dt * 1.2);
        if (yak.group.position.distanceTo(yak.targetPos) < 0.2) {
          yak.isEating = false;
        }
      }
    }
  }

  public feedYak(yakIndex: number = 0): void {
    if (this.yaks[yakIndex]) {
      const yak = this.yaks[yakIndex];
      yak.isEating = true;
      yak.targetPos.set(yak.basePos.x + (Math.random() - 0.5) * 3, yak.basePos.y, yak.basePos.z + 2.0);
    }
  }

  public dispose(): void {
    while (this.root.children.length > 0) {
      const child = this.root.children[0];
      this.root.remove(child);
    }
  }
}
